/**
 * @genesis_domain Auth Engine - Registration ViewModel
 * @genesis_version V9.1
 * @genesis_intent React 19 useActionState-based registration orchestrator. Wraps AuthContext.register()
 *   and AuthContext.verifySignup() for two-phase signup flow (register → OTP verify → workspace provision).
 * @genesis_boundaries No UI rendering, no routing logic, no DOM queries. Pure state orchestration.
 * @genesis_ai_trail V1.0: Created during Feature Sprint 1 — Multi-Tenant Onboarding. Uses native React 19
 *   useActionState for declarative pending state. No useState loading flags. Routes workspace creation
 *   through centralized apiRequest().
 * @genesis_ai_trail V1.1: Two-phase reducer added for 8-digit OTP verification flow. Phase 1 registers
 *   via AuthContext.register(), stores email. Phase 2 detects OTP token in formData, calls
 *   AuthContext.verifySignup(), then provisions workspace via apiRequest().
 */

import { useActionState } from 'react';

import { API_ROUTES } from '@/config/api.routes.js';
import { useAuth } from '@/context/AuthContext';
import { apiRequest } from '@/core/api/apiClient.js';
import { AuthRegisterSchema, type AuthRegister } from '@shared/schemas';

// ─── Zod OTP Schema ──────────────────────────────────────────────────────────

const OTP_REGEX = /^\d{8}$/;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface RegistrationState {
    success: boolean;
    error: string | null;
    isVerificationPending: boolean;
    email: string;
}

// ─── ViewModel ───────────────────────────────────────────────────────────────

export function useRegistrationViewModel() {
    const { register, verifySignup } = useAuth();

    const [state, formAction, isPending] = useActionState<RegistrationState, FormData>(
        async (_prevState: RegistrationState, formData: FormData): Promise<RegistrationState> => {
            // ── Phase Detection ──────────────────────────────────────────
            // If the form contains a 'token' field, we are in Phase 2 (OTP Verification).
            const token = (formData.get('token') as string)?.trim() ?? '';

            if (token) {
                // ── Phase 2: Verify OTP ──────────────────────────────────
                const email = _prevState.email;
                if (!email) {
                    return { success: false, error: 'Session expired. Please register again.', isVerificationPending: false, email: '' };
                }

                // Validate OTP: strictly 8 digits
                if (!OTP_REGEX.test(token)) {
                    return { success: false, error: 'Verification code must be exactly 8 digits.', isVerificationPending: true, email };
                }

                try {
                    // Verify the OTP — AuthContext injects the session on success
                    await verifySignup(email, token);

                    // Phase 2b: Provision default personal workspace
                    await apiRequest(API_ROUTES.WORKSPACES.BASE, {
                        method: 'POST',
                        body: JSON.stringify({
                            name: 'My Recipe Vault',
                            isPersonal: true,
                        }),
                    });

                    return { success: true, error: null, isVerificationPending: false, email: '' };
                } catch (err) {
                    const message =
                        err instanceof Error ? err.message : 'Verification failed. Please try again.';
                    return { success: false, error: message, isVerificationPending: true, email };
                }
            }

            // ── Phase 1: Registration ────────────────────────────────────
            const rawData = {
                name: (formData.get('name') as string)?.trim() ?? '',
                email: (formData.get('email') as string)?.trim() ?? '',
                password: (formData.get('password') as string) ?? '',
            };

            // ── Validation ───────────────────────────────────────────────
            const parseResult = AuthRegisterSchema.safeParse(rawData);
            if (!parseResult.success) {
                return { success: false, error: parseResult.error.issues[0].message, isVerificationPending: false, email: '' };
            }
            const validData: AuthRegister = parseResult.data;

            try {
                // Phase 1: Register via Supabase Auth
                const result = await register(validData.email, validData.password, validData.name);

                // If email confirmations are enabled, session is null — user must verify first
                if (result.requiresVerification) {
                    return { success: false, error: null, isVerificationPending: true, email: validData.email };
                }

                // Phase 1b: Create default personal workspace (no verification needed)
                await apiRequest(API_ROUTES.WORKSPACES.BASE, {
                    method: 'POST',
                    body: JSON.stringify({
                        name: 'My Recipe Vault',
                        isPersonal: true,
                    }),
                });

                return { success: true, error: null, isVerificationPending: false, email: '' };
            } catch (err) {
                const message =
                    err instanceof Error ? err.message : 'Registration failed. Please try again.';
                return { success: false, error: message, isVerificationPending: false, email: '' };
            }
        },
        { success: false, error: null, isVerificationPending: false, email: '' },
    );

    return { state, formAction, isPending };
}
